//@flow
import * as Immutable from 'immutable';
import moment from 'moment-timezone';

import { DayShift } from 'common/models/flow/DayShift';
import type { DayShiftModelType } from 'common/models/flow/DayShift';
import { TimeSlot } from './TimeSlot';
import { BreakShift } from './BreakShift';

import {
    validatable,
    validate,
    validateAll,
    softValidateAll
} from 'common/utils/ValidationUtil';
import I18nHelper from 'common/helpers/I18nHelper';
import { AddressLatLng } from './AddressLatLng';
import { DateTimeFormat } from 'common/utils/TimeUtils';

const translationsNamespace = ['translations', 'validations'];

const nsTranslate = I18nHelper.getTranslate(translationsNamespace);
@validatable
class InternalDayShift extends DayShift {
    static fromJS(json: DayShiftModelType): InternalDayShift {
        const state: Object = Object.assign({}, json);
        state.slot = TimeSlot.fromJS(state.slot);
        state.breaks = state.breaks
            ? Immutable.List(state.breaks).map(item => BreakShift.fromJS(item))
            : state.breaks;
        state.startLocation = state.startLocation
            ? AddressLatLng.fromJS(state.startLocation)
            : state.startLocation;
        state.endLocation = state.endLocation
            ? AddressLatLng.fromJS(state.endLocation)
            : state.endLocation;
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        const { slot, breaks, startLocation, endLocation } = this;
        if (slot && slot.hasValidationErrors()) {
            return true;
        }
        if (breaks) {
            return breaks.some(breakShift => breakShift.hasValidationErrors());
        }
        if (startLocation) {
            return startLocation.hasValidationErrors();
        }
        if (endLocation) {
            return endLocation.hasValidationErrors();
        }
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { slot, breaks, startLocation, endLocation } = this;
        if (slot) {
            slot = slot.setAllValidationErrors();
        }
        if (breaks) {
            breaks = breaks.map(breakShift =>
                breakShift.setAllValidationErrors()
            );
        }
        if (startLocation) {
            startLocation = startLocation.setAllValidationErrors();
        }
        if (endLocation) {
            endLocation = endLocation.setAllValidationErrors();
        }
        return this.setSlot(slot)
            .setBreaks(breaks)
            .setStartLocation(startLocation)
            .setEndLocation(endLocation);
    }

    @validate
    setSlot(slot: TimeSlot): this {
        return super.setSlot(slot);
    }

    validateSlot(): string {
        const { slot, breaks, startLocation, endLocation } = this;
        if (slot && slot.hasValidationErrors()) {
            return (
                slot.getValidationMessage('start') ||
                slot.getValidationMessage('end')
            );
        }
        if (breaks && breaks.size > 0 && slot.isEmpty()) {
            return nsTranslate('timeSlot.slotRequiredWhenBreaksPresent');
        }
        if (startLocation && startLocation.hasValidationErrors()) {
            return (
                startLocation.latLng.getValidationMessage('lat') ||
                startLocation.latLng.getValidationMessage('lng')
            );
        }
        if (endLocation && endLocation.hasValidationErrors()) {
            return (
                endLocation.latLng.getValidationMessage('lat') ||
                endLocation.latLng.getValidationMessage('lng')
            );
        }
    }

    @validate
    setBreaks(breaks: Immutable.List<BreakShift>): this {
        return super.setBreaks(breaks);
    }

    validateBreaks(): ?string {
        const { breaks } = this;
        if (breaks) {
            const breakSlot = breaks.find(breakSlot =>
                breakSlot.hasValidationErrors()
            );

            if (breakSlot) {
                return (
                    breakSlot.getValidationMessage('startShiftInstant') ||
                    breakSlot.getValidationMessage('endShiftInstant') ||
                    breakSlot.getValidationMessage('duration')
                );
            }
        }
    }

    getShortCode() {
        return nsTranslate(`daysShortCode.${this.dayOfWeek}`);
    }

    validateBreaksWithSlot(): ?string {
        const {
            breaks,
            endDayOffset,
            slot: { start, end }
        } = this;
        if (this.slot.hasValidationErrors()) {
            return this.validateSlot();
        }
        const shiftStart = moment(start, DateTimeFormat.HTML5_FMT_TIME);
        const shiftEnd = moment(end, DateTimeFormat.HTML5_FMT_TIME).add(
            endDayOffset,
            'd'
        );
        let error = null;
        for (let i = 0; i < breaks?.size; i++) {
            const singleBreak = breaks.get(i);
            const { startShiftInstant, endShiftInstant } = singleBreak;
            const breakError =
                singleBreak.validateBreakStartWithShift({
                    shiftStart,
                    startShiftInstant
                }) ||
                singleBreak.validateBreakEndWithShift({
                    shiftEnd,
                    endShiftInstant
                });
            if (breakError) {
                error = breakError;
                break;
            }
        }
        return error;
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedDayShift = super.defaultSanitize();
        if (sanitizedDayShift) {
            const slot = sanitizedDayShift.slot?.defaultSanitize(); // TimeSlot model
            //Optional chaning is breaking codegen
            const breaks = sanitizedDayShift.breaks
                ? sanitizedDayShift.breaks.map(
                      breakShift => breakShift?.defaultSanitize() // breakShift model
                  )
                : null;
            const startLocation = sanitizedDayShift.startLocation?.defaultSanitize(); // AddressLatLng model
            const endLocation = sanitizedDayShift.endLocation?.defaultSanitize(); // AddressLatLng model
            return sanitizedDayShift
                .setStartLocation(startLocation)
                .setEndLocation(endLocation)
                .setBreaks(breaks)
                .setSlot(slot);
        }
        return sanitizedDayShift;
    }

    get slot(): TimeSlot {
        return this._state.get('slot');
    }

    get breaks(): ?Immutable.List<BreakShift> {
        return this._state.get('breaks');
    }

    get startLocation(): ?AddressLatLng {
        return this._state.get('startLocation');
    }

    get endLocation(): ?AddressLatLng {
        return this._state.get('endLocation');
    }
}

export { InternalDayShift as DayShift };
